import React, { useState } from 'react'
import { FileText, Copy, CheckCircle } from 'lucide-react'
import { getSMSTemplates, SMSTemplate } from '../../services/communicationsService'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'

interface SMSTemplatesProps {
  onSelectTemplate: (template: SMSTemplate) => void
}

const SMSTemplates: React.FC<SMSTemplatesProps> = ({ onSelectTemplate }) => {
  const [copiedId, setCopiedId] = useState<string | null>(null)
  const templates = getSMSTemplates()

  const categories = Array.from(new Set(templates.map(t => t.category)))

  const copyToClipboard = (content: string, id: string) => {
    navigator.clipboard.writeText(content)
    setCopiedId(id)
    setTimeout(() => setCopiedId(null), 2000)
  }

  const getCategoryVariant = (category: string): "default" | "destructive" | "outline" | "secondary" => {
    const variants: Record<string, "default" | "destructive" | "outline" | "secondary"> = {
      onboarding: 'default',
      events: 'secondary',
      finance: 'outline',
      special_occasions: 'secondary',
      pastoral_care: 'outline',
      urgent: 'destructive',
      announcements: 'default',
    }
    return variants[category] || 'secondary'
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" />
              SMS Templates
            </CardTitle>
            <Badge variant="secondary">{templates.length} templates</Badge>
          </div>
          <CardDescription>
            Pre-built message templates to save time. Click "Use Template" to fill in variables and send.
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-6">

          {/* Templates by Category */}
          {categories.map(category => {
            const categoryTemplates = templates.filter(t => t.category === category)

            return (
              <div key={category} className="space-y-3">
                <div className="flex items-center gap-2">
                  <Badge variant={getCategoryVariant(category)} className="capitalize">
                    {category.replace(/_/g, ' ')}
                  </Badge>
                  <span className="text-sm text-muted-foreground">({categoryTemplates.length})</span>
                </div>

                <div className="space-y-3">
                  {categoryTemplates.map(template => (
                    <div
                      key={template.id}
                      className="p-4 rounded-md border bg-card hover:bg-accent transition-colors"
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <h5 className="font-medium mb-1">{template.name}</h5>
                          <div className="flex flex-wrap gap-1">
                            {template.variables.map(variable => (
                              <Badge
                                key={variable}
                                variant="outline"
                                className="text-xs font-mono"
                              >
                                {`{${variable}}`}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      </div>

                      <div className="text-sm mb-3 p-3 bg-muted rounded-md">
                        {template.content}
                      </div>

                      <div className="flex items-center justify-between">
                        <span className="text-xs text-muted-foreground">
                          {template.content.length} characters • ~{Math.ceil(template.content.length / 160)} SMS
                        </span>

                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => copyToClipboard(template.content, template.id)}
                          >
                            {copiedId === template.id ? (
                              <>
                                <CheckCircle className="w-3 h-3 mr-1" />
                                Copied
                              </>
                            ) : (
                              <>
                                <Copy className="w-3 h-3 mr-1" />
                                Copy
                              </>
                            )}
                          </Button>
                          <Button
                            size="sm"
                            onClick={() => onSelectTemplate(template)}
                          >
                            Use Template
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )
          })}
        </CardContent>
      </Card>

      {/* Usage Guide */}
      <Card>
        <CardHeader>
          <CardTitle>How to Use Templates</CardTitle>
        </CardHeader>
        <CardContent>
          <ol className="space-y-2 text-sm">
            <li className="flex items-start gap-2">
              <Badge variant="default" className="flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center p-0 text-xs">1</Badge>
              <span>Click "Use Template" on any template above</span>
            </li>
            <li className="flex items-start gap-2">
              <Badge variant="default" className="flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center p-0 text-xs">2</Badge>
              <span>Fill in the required variables (shown in {'{curly braces}'})</span>
            </li>
            <li className="flex items-start gap-2">
              <Badge variant="default" className="flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center p-0 text-xs">3</Badge>
              <span>Apply the template to generate your message</span>
            </li>
            <li className="flex items-start gap-2">
              <Badge variant="default" className="flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center p-0 text-xs">4</Badge>
              <span>Select recipients and send your campaign</span>
            </li>
          </ol>
        </CardContent>
      </Card>

      {/* Template Variables Reference */}
      <Card>
        <CardHeader>
          <CardTitle>Common Variables</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {[
              { var: 'name', desc: "Recipient's name" },
              { var: 'church_name', desc: 'Your church name' },
              { var: 'event_name', desc: 'Event title' },
              { var: 'event_time', desc: 'Event time' },
              { var: 'event_location', desc: 'Event location' },
              { var: 'service_time', desc: 'Service time' },
              { var: 'amount', desc: 'Money amount' },
              { var: 'receipt_number', desc: 'Receipt #' },
              { var: 'date', desc: 'Date' },
            ].map(({ var: variable, desc }) => (
              <div key={variable} className="p-3 rounded-md border bg-card">
                <div className="text-xs font-mono text-primary">{`{${variable}}`}</div>
                <div className="text-xs text-muted-foreground mt-1">{desc}</div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

export default SMSTemplates
